/**
 * Request Queue and Throttler
 * 
 * Manages concurrent requests to prevent overwhelming the network.
 * Implements request queuing, throttling, and rate limiting.
 * 
 * Based on HAR analysis findings:
 * - 19 rapid-fire requests (<100ms apart)
 * - Average 906ms between requests
 * - Need to limit concurrent requests and implement queuing
 */

export interface QueuedRequest {
  id: string;
  execute: () => Promise<any>;
  priority: 'high' | 'normal' | 'low';
  resolve: (value: any) => void;
  reject: (error: any) => void;
  timestamp: number;
}

interface RequestQueueConfig {
  maxConcurrent: number;        // Max concurrent requests (default: 6)
  minInterval: number;          // Min time between requests in ms (default: 100)
  maxQueueSize: number;         // Max queue size before rejecting (default: 100)
  timeout: number;              // Request timeout in ms (default: 30000)
}

class RequestQueue {
  private queue: QueuedRequest[] = [];
  private running: Set<string> = new Set();
  private lastRequestTime: number = 0;
  private config: RequestQueueConfig;

  constructor(config?: Partial<RequestQueueConfig>) {
    this.config = {
      maxConcurrent: 6,        // HTTP/2 can handle more, but limit to prevent overload
      minInterval: 100,        // 100ms minimum between requests
      maxQueueSize: 100,
      timeout: 30000,
      ...config,
    };
  }

  /**
   * Add a request to the queue
   */
  async enqueue<T>(
    execute: () => Promise<T>,
    priority: 'high' | 'normal' | 'low' = 'normal'
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      // Check queue size
      if (this.queue.length >= this.config.maxQueueSize) {
        reject(new Error('Request queue is full'));
        return;
      }

      const request: QueuedRequest = {
        id: `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        execute,
        priority,
        resolve,
        reject,
        timestamp: Date.now(),
      };

      // Insert based on priority
      if (priority === 'high') {
        // High priority goes to front
        this.queue.unshift(request);
      } else if (priority === 'low') {
        // Low priority goes to back
        this.queue.push(request);
      } else {
        // Normal priority goes after high, before low
        const firstLowIndex = this.queue.findIndex(r => r.priority === 'low');
        if (firstLowIndex === -1) {
          this.queue.push(request);
        } else {
          this.queue.splice(firstLowIndex, 0, request);
        }
      }

      // Process queue
      this.processQueue();
    });
  }

  /**
   * Process the queue
   */
  private async processQueue(): Promise<void> {
    // Don't process if at max concurrent
    if (this.running.size >= this.config.maxConcurrent) {
      return;
    }

    // Don't process if queue is empty
    if (this.queue.length === 0) {
      return;
    }

    // Check minimum interval
    const timeSinceLastRequest = Date.now() - this.lastRequestTime;
    if (timeSinceLastRequest < this.config.minInterval) {
      // Schedule processing after interval
      setTimeout(() => this.processQueue(), this.config.minInterval - timeSinceLastRequest);
      return;
    }

    // Get next request
    const request = this.queue.shift();
    if (!request) {
      return;
    }

    // Mark as running
    this.running.add(request.id);
    this.lastRequestTime = Date.now();

    // Execute request
    this.executeRequest(request)
      .finally(() => {
        this.running.delete(request.id);
        // Process next request
        this.processQueue();
      });
  }

  /**
   * Execute a single request
   */
  private async executeRequest(request: QueuedRequest): Promise<void> {
    try {
      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), this.config.timeout);
      });

      // Race between request and timeout
      const result = await Promise.race([
        request.execute(),
        timeoutPromise,
      ]);

      request.resolve(result);
    } catch (error) {
      request.reject(error);
    }
  }

  /**
   * Get current queue status
   */
  getStatus(): {
    queueSize: number;
    running: number;
    maxConcurrent: number;
  } {
    return {
      queueSize: this.queue.length,
      running: this.running.size,
      maxConcurrent: this.config.maxConcurrent,
    };
  }

  /**
   * Clear the queue (reject all pending requests)
   */
  clear(): void {
    const pending = [...this.queue];
    this.queue = [];
    pending.forEach(request => {
      request.reject(new Error('Request queue cleared'));
    });
  }

  /**
   * Update configuration
   */
  configure(config: Partial<RequestQueueConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

// Export singleton instance
export const requestQueue = new RequestQueue();

// Export class for custom instances
export default RequestQueue;

